<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { Overlay } from 'ol'
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style'
import CandidateInfoCard from '@/components/admin/CandidateInfoCard.vue'
import ExplorerServedContext from '@/components/admin/ExplorerServedContext.vue'
import FeatureContributionsDialog from '@/components/admin/FeatureContributionsDialog.vue'
import SimilarityWeightControls from '@/components/admin/SimilarityWeightControls.vue'
import ModelSelector from '@/components/form/ModelSelector.vue'
import MapComponent from '@/components/map/MapComponent.vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import { useFormatDate } from '@/composables/useFormatDate'
import { MAP_DEFAULT_BASELAYER, MAP_PROJECTION } from '@/constants'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { pctColor, signedPct } from '@/services/contributionFormat'
import { transform } from '@/services/map/ProjectionService'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  models: { type: Array, default: () => [] },
  defaultModel: { type: String, default: null },
  defaultConfig: { type: Object, required: true },
  weightBounds: { type: Object, default: () => ({}) },
  testCases: { type: Array, default: () => [] },
  recentEvaluations: { type: Array, default: () => [] },
  recentTraces: { type: Array, default: () => [] },
  initialSubject: { type: Object, default: () => ({ type: null, id: null }) },
})

const { findLayer } = useDetailMapHelpers()
const { formatDate } = useFormatDate()

// ─── Subject + model selection ──────────────────────────────────────────────
const subjectTypeOptions = [
  { label: 'Testjuhtum', value: 'test_case' },
  { label: 'Hindamine', value: 'evaluation' },
  { label: 'Ennustus (trace)', value: 'trace' },
]

const subjectType = ref(props.initialSubject?.type || 'test_case')
const subjectId = ref(props.initialSubject?.id || null)

const subjectOptions = computed(() => {
  if (subjectType.value === 'test_case') {
    return props.testCases.map(c => ({
      label: `${c.name} · ${formatPrice(c.manual_valuation)}`,
      value: c.id,
    }))
  }
  if (subjectType.value === 'evaluation') {
    return props.recentEvaluations.map(e => ({
      label: e.label,
      value: e.id,
      // Mirrors the web URL (quick-valuation/6780 vs evaluation/123) so a case
      // reported by users can be found by its id or kind.
      meta: `${e.kind}/${e.id}`,
      created_at: e.created_at,
    }))
  }
  return props.recentTraces.map(t => ({
    label: `${t.label} · ${formatPrice(t.predicted_value)}`,
    value: t.id,
  }))
})

// Pre-select the backend default EBM model, falling back to the latest EBM
// (CatBoost similarity models are excluded by ModelSelector's type filter).
const latestEbmModel = (props.models || [])
  .filter(m => m?.type === 'ebm')
  .slice()
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.model
const modelType = ref(props.defaultModel || latestEbmModel || null)

const toast = useToast()

// Local model list so "Uuenda mudelid" (ML API hot reload of freshly staged
// autoresearch candidates) can update the picker without an Inertia reload.
const modelsList = ref(props.models || [])

// Served price-CatBoost ids for the "Sarnasuse mudel" selector (DERIV
// re-rankers share the catboost type but are never similarity anchors).
const catboostModels = computed(() =>
  (modelsList.value || [])
    .filter(m => m?.type === 'catboost' && !String(m.model).startsWith('DERIV_'))
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map(m => m.model),
)
const refreshingModels = ref(false)
const refreshModels = async () => {
  refreshingModels.value = true
  try {
    const response = await fetch(route('admin.model-explorer.refresh-models'), {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
      },
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const data = await response.json()
    modelsList.value = data.models || []
    const loaded = data.reload?.loaded || []
    const failed = data.reload?.failed || {}
    toast.add({
      severity: Object.keys(failed).length ? 'warn' : 'success',
      summary: 'Mudelid uuendatud',
      detail: loaded.length
        ? `Uued mudelid: ${loaded.join(', ')}`
        : Object.keys(failed).length
          ? `Tagasi lükatud: ${Object.keys(failed).join(', ')}`
          : 'Uusi mudeleid ei leitud.',
      life: 6000,
    })
  } catch {
    toast.add({ severity: 'error', summary: 'Mudelite uuendamine ebaõnnestus', life: 5000 })
  } finally {
    refreshingModels.value = false
  }
}

const poolLimitOptions = [
  { label: '200 lähimat', value: 200 },
  { label: '400 lähimat', value: 400 },
  { label: '800 lähimat', value: 800 },
  { label: 'Kõik', value: 2000 },
]
const candidatePoolLimit = ref(400)

// ─── Live base + admin overrides (RED-356) ──────────────────────────────────
// A request that carries only the subject (and the pool size) makes redata-api see exactly what
// the live Kiirhindamine sends for the valuation's owner; every other field is an admin override
// laid on top. So every control starts at, and is compared against, `base`: the live values —
// the controller's defaultConfig (default model) until the first run, then each run's
// `served.base_ui` (the calibration of the model that ran) and `served.model.served`. A run sends
// only the controls that differ from it.
// Live sends no rerank shortlist / rank weights (redata-api uses its own); these are the
// explorer's starting values, sent only once changed under derivation_error ranking.
const RERANK_SHORTLIST_DEFAULT = 50
const RERANK_WEIGHTS_DEFAULT = Object.freeze({ error: 1.0, similarity: 0, geo: 0, adjustment: 0 })

// Fallbacks mirror the controller's QuickValuation defaults — they only fire defensively when a
// key is absent.
const baseFrom = (ui, model) => ({
  model_type: model ?? null,
  embed_weights: [...(ui?.embed_weights || [45, 61, 47, 51])],
  zdist_weight: ui?.zdist_weight ?? 52,
  price_diff_weight: ui?.price_diff_weight ?? 1.4,
  rikastatus_weight: ui?.rikastatus_weight ?? 3.0,
  recent_months: ui?.recent_months ?? 12,
  similarity_price_model: ui?.similarity_price_model || 'catboost',
  n_similar: ui?.n_similar ?? 3,
  pool_residual_gate: {
    lower_pct: ui?.pool_residual_gate?.lower_pct ?? null,
    upper_pct: ui?.pool_residual_gate?.upper_pct ?? null,
  },
  rerank_mode: ui?.rerank_mode || 'l1',
  rerank_shortlist: RERANK_SHORTLIST_DEFAULT,
  rerank_rank_weights: { ...RERANK_WEIGHTS_DEFAULT },
  completeness_gate: ui?.completeness_gate || 'off',
  impute_comparable_features: Boolean(ui?.impute_comparable_features),
  use_segment_config: true,
})
const base = ref(baseFrom(props.defaultConfig, props.defaultModel))

// UI-scale similarity weights (SimilarityWeightControls v-model).
const config = ref({
  embedWeights: [...base.value.embed_weights],
  zdistWeight: base.value.zdist_weight,
  priceDiffWeight: base.value.price_diff_weight,
  rikastatusWeight: base.value.rikastatus_weight,
  recentMonths: base.value.recent_months,
  nSimilar: base.value.n_similar,
  similarityPriceModel: base.value.similarity_price_model,
})

// EBM-residual pool gate band (%): live's own band (null = off). Clearing both inputs while live
// gates sends an explicit "off".
const residualGateLower = ref(base.value.pool_residual_gate.lower_pct)
const residualGateUpper = ref(base.value.pool_residual_gate.upper_pct)
// RED-262: completeness pool filter — which candidates may serve, by the five
// Kohandustabel condition families (ehitise/hooneosa seisukord, parkimine,
// panipaik, rõdu). 'off' = all candidates.
const completenessGate = ref(base.value.completeness_gate)
const completenessGateOptions = [
  { label: 'Kõik kandidaadid', value: 'off' },
  { label: 'Rikastatud + AI', value: 'imputed' },
  { label: 'Ainult rikastatud', value: 'observed' },
  // RED-262 "Automaatne": the API evaluates both pools and serves the one whose
  // weighted average lands closer to the raw EBM prediction (tie → observed).
  { label: 'Automaatne', value: 'auto' },
]
// Adjustment coherence: comparables' AI-imputed condition values enter the model features.
const imputeComparableFeatures = ref(base.value.impute_comparable_features)
// Comparable ranking mode; the API falls back to L1 when no DERIV bundle serves.
const rerankMode = ref(base.value.rerank_mode)
const rerankOptions = [
  { label: 'L1 skoor', value: 'l1' },
  { label: 'Tuletusviga (mudel)', value: 'derivation_error' },
]
// Stage-1 retrieval size: only the L1 top-N are eligible for re-ranking.
const rerankShortlist = ref(RERANK_SHORTLIST_DEFAULT)
// Rank-composite weights for the final ordering (scale-free rank blend).
// Defaults = pure model preference.
const rerankWeights = ref({ ...RERANK_WEIGHTS_DEFAULT })
const rerankWeightFields = [
  { key: 'error', label: 'viga', tip: 'Tuletusvea mudeli eelistus' },
  { key: 'similarity', label: 'sarn', tip: 'L1 sarnasus (Koondskoor)' },
  { key: 'geo', label: 'geo', tip: 'Geograafiline kaugus — eelista lähemaid' },
  { key: 'adjustment', label: 'koh', tip: 'Kohanduse suurus (|pred_i/pred_j − 1|) — eelista vähima kohandusega võrdlusi' },
]
// Live always routes the subject through the active segmentation (RED-262) — an applied
// segment's tuned weights override the served ones (shown in the result). Off = experiment
// with the raw weights.
const useSegmentConfig = ref(true)
const appliedSegment = computed(() => result.value?.segment || null)

// Every control as one field: its API key, how to read and write it, how to compare it with
// the live value. `sent: false` = display only; `active` = only meaningful in that mode.
const sameNumber = (a, b) =>
  a == null || b == null ? a == null && b == null : Math.abs(Number(a) - Number(b)) < 1e-6
const sameNumbers = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => sameNumber(v, b[i]))
const sameValue = (a, b) => (a ?? null) === (b ?? null)
const sameFlag = (a, b) => Boolean(a) === Boolean(b)
const sameBand = (a, b) =>
  sameNumber(a?.lower_pct ?? null, b?.lower_pct ?? null) && sameNumber(a?.upper_pct ?? null, b?.upper_pct ?? null)
const sameRankWeights = (a, b) =>
  Object.keys(RERANK_WEIGHTS_DEFAULT).every(k => sameNumber(a?.[k] ?? null, b?.[k] ?? null))
const isDerivationRerank = () => rerankMode.value === 'derivation_error'
const yesNo = value => (value ? 'sees' : 'väljas')
const optionLabel = (options, value) => options.find(o => o.value === value)?.label ?? value

const controlFields = [
  {
    key: 'model_type',
    label: 'mudel',
    get: () => modelType.value,
    set: v => {
      // Live with no pinned default would refuse; keep the admin's pick rather than blank it.
      if (v != null) {
        modelType.value = v
      }
    },
    same: sameValue,
    live: v => v ?? 'pole kinnitatud',
  },
  {
    key: 'embed_weights',
    label: 'mudeli tunnused',
    get: () => config.value.embedWeights,
    set: v => {
      config.value.embedWeights = [...v]
    },
    same: sameNumbers,
  },
  {
    key: 'zdist_weight',
    label: 'geograafiline kaugus',
    get: () => config.value.zdistWeight,
    set: v => {
      config.value.zdistWeight = v
    },
    same: sameNumber,
  },
  {
    key: 'price_diff_weight',
    label: 'hinna sarnasus',
    get: () => config.value.priceDiffWeight,
    set: v => {
      config.value.priceDiffWeight = v
    },
    same: sameNumber,
  },
  {
    key: 'rikastatus_weight',
    label: 'rikastatud andmed',
    get: () => config.value.rikastatusWeight,
    set: v => {
      config.value.rikastatusWeight = v
    },
    same: sameNumber,
  },
  {
    key: 'recent_months',
    label: 'ajaperiood',
    get: () => config.value.recentMonths,
    set: v => {
      config.value.recentMonths = v
    },
    same: sameNumber,
  },
  {
    key: 'similarity_price_model',
    label: 'sarnasuse mudel',
    get: () => config.value.similarityPriceModel,
    set: v => {
      config.value.similarityPriceModel = v
    },
    same: sameValue,
  },
  {
    // Display only: live never sends a comparable count (redata-api uses its own ~3).
    key: 'n_similar',
    sent: false,
    get: () => config.value.nSimilar,
    set: v => {
      config.value.nSimilar = v
    },
    same: sameNumber,
  },
  {
    key: 'pool_residual_gate',
    label: 'EBM-jäägi piir',
    get: () => ({ lower_pct: residualGateLower.value ?? null, upper_pct: residualGateUpper.value ?? null }),
    set: v => {
      residualGateLower.value = v?.lower_pct ?? null
      residualGateUpper.value = v?.upper_pct ?? null
    },
    same: sameBand,
    live: v => (v?.lower_pct == null && v?.upper_pct == null ? 'väljas' : `${v.lower_pct ?? '–'} … ${v.upper_pct ?? '–'} %`),
  },
  {
    key: 'rerank_mode',
    label: 'järjestus',
    get: () => rerankMode.value,
    set: v => {
      rerankMode.value = v
    },
    same: sameValue,
    live: v => optionLabel(rerankOptions, v),
  },
  {
    key: 'rerank_shortlist',
    label: 'lühinimekiri',
    active: isDerivationRerank,
    get: () => rerankShortlist.value,
    set: v => {
      rerankShortlist.value = v
    },
    same: sameNumber,
    live: v => `${v} (API vaikimisi)`,
  },
  {
    key: 'rerank_rank_weights',
    label: 'järjestuskaalud',
    active: isDerivationRerank,
    get: () => rerankWeights.value,
    set: v => {
      rerankWeights.value = { ...v }
    },
    same: sameRankWeights,
    live: v => Object.values(v).join(' / '),
  },
  {
    key: 'completeness_gate',
    label: 'andmete täielikkus',
    get: () => completenessGate.value,
    set: v => {
      completenessGate.value = v
    },
    same: sameValue,
    live: v => optionLabel(completenessGateOptions, v),
  },
  {
    key: 'impute_comparable_features',
    label: 'AI-tunnused võrdlustele',
    get: () => imputeComparableFeatures.value,
    set: v => {
      imputeComparableFeatures.value = Boolean(v)
    },
    same: sameFlag,
    live: yesNo,
  },
  {
    // Live always applies the segment, so only "off" is ever sent.
    key: 'use_segment_config',
    label: 'segment väljas',
    get: () => useSegmentConfig.value,
    set: v => {
      useSegmentConfig.value = Boolean(v)
    },
    same: sameFlag,
    live: yesNo,
  },
]

const cloneValue = v => (v != null && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v)
// The fields that reach the wire in the current ranking mode.
const effectiveFields = () => controlFields.filter(f => f.sent !== false && (!f.active || f.active()))

// The controls the admin moved off live — exactly what the next run sends.
const overriddenFields = computed(() => effectiveFields().filter(f => !f.same(f.get(), base.value[f.key])))
const overriddenKeys = computed(() => overriddenFields.value.map(f => f.key))
const isOverridden = key => overriddenKeys.value.includes(key)
const overrideLabel = computed(() => overriddenFields.value.map(f => f.label).join(', '))
// Plain label per request field, for the served block's list of the fields the run changed.
const fieldLabels = Object.fromEntries(controlFields.filter(f => f.label).map(f => [f.key, f.label]))
const labelClass = key => (isOverridden(key) ? 'text-orange-600 font-medium' : 'text-gray-600')
const liveTitle = key => {
  if (!isOverridden(key)) {
    return undefined
  }
  const field = controlFields.find(f => f.key === key)
  const value = base.value[key]
  return `Muudetud — live: ${field?.live ? field.live(value) : value}`
}
const overridePayload = () =>
  Object.fromEntries(overriddenFields.value.map(f => [f.key, cloneValue(f.get())]))
const snapshotControls = () => Object.fromEntries(controlFields.map(f => [f.key, cloneValue(f.get())]))

// After a run: every control that showed the live value when the run started — and that the
// admin has not touched since — moves to the new live value (a model switch re-seeds that
// model's own calibration). Overridden controls, and anything edited while the run was in
// flight, keep the admin's value. Returns the controls the shown result ran with.
const reseedFromServed = (servedBlock, baseAtRun, snapshot) => {
  if (!servedBlock?.base_ui) {
    return snapshot
  }
  const nextBase = baseFrom(servedBlock.base_ui, servedBlock.model?.served)
  const ran = {}
  for (const f of controlFields) {
    const untouched = f.same(f.get(), snapshot[f.key])
    if (untouched && f.same(snapshot[f.key], baseAtRun[f.key])) {
      f.set(cloneValue(nextBase[f.key]))
    }
    ran[f.key] = untouched ? cloneValue(f.get()) : snapshot[f.key]
  }
  base.value = nextBase
  return ran
}

// The controls (and pool size) the shown result ran with — drives the "not run yet" hint.
const ranControls = ref(null)
const hasUnrunChanges = computed(() => {
  const ran = ranControls.value
  if (!ran || !result.value) {
    return false
  }
  return (
    ran.poolLimit !== candidatePoolLimit.value ||
    effectiveFields().some(f => !f.same(f.get(), ran.values[f.key]))
  )
})

// ─── Analysis run ───────────────────────────────────────────────────────────
const loading = ref(false)
// Which action shows the progress: 'run' (the Run button, also the automatic runs), 'reset',
// or 'fresh' (rebuild the subject's features).
const runSource = ref(null)
const errorMessage = ref(null)
const result = ref(null)
// The subject kind the shown result belongs to (the selector may already point elsewhere).
const resultSubjectType = ref(null)
const focusedId = ref(null)

const mapRef = ref(null)

const canRun = computed(() => subjectType.value && subjectId.value)

// Only the latest run may land: a newer one (another subject, a reset) aborts the one in flight.
let runSeq = 0
let runAbort = null

const runAnalysis = async ({ source = 'run', freshFeatures = false } = {}) => {
  if (!canRun.value) {
    errorMessage.value = 'Vali esmalt analüüsitav objekt.'
    return
  }
  runAbort?.abort()
  const controller = new AbortController()
  runAbort = controller
  const seq = ++runSeq

  const overrides = overridePayload()
  const snapshot = snapshotControls()
  const baseAtRun = base.value
  const poolLimit = candidatePoolLimit.value
  const snapshotSubjectType = subjectType.value

  loading.value = true
  runSource.value = source
  errorMessage.value = null

  try {
    const response = await fetch(route('admin.model-explorer.analyze'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
      },
      // Subject + pool size alone = the live request; the rest are the admin's overrides.
      body: JSON.stringify({
        subject_type: subjectType.value,
        subject_id: subjectId.value,
        candidate_pool_limit: poolLimit,
        ...overrides,
        ...(freshFeatures ? { fresh_features: true } : {}),
      }),
      signal: controller.signal,
    })

    const data = await response.json()
    if (seq !== runSeq) {
      return
    }
    if (!data.success) {
      errorMessage.value = data.error || data.message || 'Analüüs ebaõnnestus'
      return
    }

    focusedId.value = null
    closeDetail()
    hoverCandidate.value = null
    result.value = data.result
    resultSubjectType.value = snapshotSubjectType
    ranControls.value = {
      poolLimit,
      values: reseedFromServed(data.result?.served, baseAtRun, snapshot),
    }
    tableOpen.value = true
    renderMap()
  } catch (e) {
    if (seq === runSeq && e.name !== 'AbortError') {
      errorMessage.value = e.message
    }
  } finally {
    if (seq === runSeq) {
      loading.value = false
      runSource.value = null
    }
  }
}

// "Taasta live seaded": every control back to the live value, then run.
const restoreLive = () => {
  for (const f of controlFields) {
    f.set(cloneValue(base.value[f.key]))
  }
  if (canRun.value) {
    runAnalysis({ source: 'reset' })
  }
}

const rebuildFeatures = () => runAnalysis({ source: 'fresh', freshFeatures: true })

// A newly picked subject runs at once, on live values: overrides made for the previous subject
// are cleared first, so the new one never sends a request live would not (said in the hint line).
// Sliders never run on their own.
const clearedOverrides = ref(0)
watch(subjectId, id => {
  if (!id) {
    return
  }
  const cleared = overriddenFields.value.length
  for (const f of controlFields) {
    f.set(cloneValue(base.value[f.key]))
  }
  clearedOverrides.value = cleared
  runAnalysis()
})
// The note goes once the admin changes a control again.
watch(
  () => overriddenFields.value.length,
  count => {
    if (count > 0) {
      clearedOverrides.value = 0
    }
  },
)
// One short line, so the space reserved for it holds; the full sentence is the tooltip.
const runHint = computed(() => {
  if (hasUnrunChanges.value) {
    return { text: 'Muudatused on käivitamata', title: null }
  }
  if (clearedOverrides.value > 0) {
    return {
      text: `Eelmise objekti muudatused (${clearedOverrides.value}) tühistati`,
      title: 'Uus objekt käivitati live seadetega, eelmise objekti muudatused tühistati.',
    }
  }
  return null
})

const served = computed(() => result.value?.served || null)
// What the live result page shows instead of the comparables below, when it does.
const liveAnswerNotice = computed(() => {
  const s = served.value
  if (s?.live_answer === 'direct') {
    return {
      severity: 'warn',
      text: `Live hindab selle otsemudeliga (ilma võrdlustehinguteta): ${formatPrice(s.direct?.predicted_value)}. Allolev valim on võrdlustehingute tee, mida live ei näita.`,
    }
  }
  if (s?.live_answer === 'gate_backup_direct') {
    return {
      severity: 'info',
      text: `Värav lükkas võrdlustehingud tagasi — live näitab otsemudeli hinda ${formatPrice(s.gate?.direct_price ?? s.direct?.predicted_value)}.`,
    }
  }
  // RED-262 gates v1: the owner's flag put live on the county locked-scale model.
  if (s?.live_answer === 'gates_direct') {
    return {
      severity: 'info',
      text: `Väravad v1 — live näitab lukus skaala mudeli hinda ${formatPrice(s.gates?.headline_price)} (hinne ${s.gates?.grade}) koos vahemikuga. Allolev valim on võrdlustehingute tee, mille hinda live ei näita.`,
    }
  }
  if (s?.live_answer === 'gates_valuer') {
    return {
      severity: 'warn',
      text: 'Väravad v1 — live ei näita automaatset hinda, hinna saamiseks on vaja hindaja hinnangut.',
    }
  }
  return null
})

// ─── Map rendering ──────────────────────────────────────────────────────────
const mapview = {
  baseLayers: [findLayer(MAP_DEFAULT_BASELAYER, false)],
  baseLayer: MAP_DEFAULT_BASELAYER,
  center: transform('fromLonLat', [24.75, 59.43], MAP_PROJECTION),
}

const toMapCoord = coords => transform('fromLonLat', coords, MAP_PROJECTION)

const candidatesById = computed(() => {
  const byId = new Map()
  for (const c of result.value?.candidates || []) {
    byId.set(c.id, c)
  }
  return byId
})

const buildFeatures = () => {
  const r = result.value
  if (!r) {
    return []
  }
  const features = []

  if (r.subject?.coords) {
    features.push({
      type: 'Feature',
      properties: { kind: 'subject', label: r.subject.label },
      geometry: { type: 'Point', coordinates: toMapCoord(r.subject.coords) },
    })
  }

  const scores = r.candidates.filter(c => c.coords && c.score != null).map(c => c.score)
  const min = scores.length ? Math.min(...scores) : 0
  const max = scores.length ? Math.max(...scores) : 1
  const span = max > min ? max - min : 1

  for (const c of r.candidates) {
    if (!c.coords) {
      continue
    }
    features.push({
      type: 'Feature',
      properties: {
        kind: c.was_selected ? 'selected' : 'candidate',
        id: c.id,
        rank: c.rank,
        scoreNorm: c.score != null ? (c.score - min) / span : 0.5,
      },
      geometry: { type: 'Point', coordinates: toMapCoord(c.coords) },
    })
  }
  return features
}

const scoreColor = (norm, alpha = 1) => {
  const hue = (1 - Math.min(Math.max(norm, 0), 1)) * 120 // best (low score) → green, worst → red
  return `hsla(${hue}, 70%, 48%, ${alpha})`
}

const featureStyle = feature => {
  const kind = feature.get('kind')
  const hovered = feature.get('hovered')

  if (kind === 'subject') {
    return new Style({
      image: new CircleStyle({
        radius: hovered ? 11 : 9,
        fill: new Fill({ color: '#DB3848' }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      zIndex: 100,
    })
  }

  const norm = feature.get('scoreNorm') ?? 0.5
  const emph = feature.get('id') === focusedId.value || hovered

  if (kind === 'selected') {
    return new Style({
      image: new CircleStyle({
        radius: emph ? 10 : 7,
        fill: new Fill({ color: scoreColor(norm) }),
        stroke: new Stroke({ color: '#1d4ed8', width: 3 }),
      }),
      text: new Text({
        text: String(feature.get('rank') ?? ''),
        font: 'bold 10px sans-serif',
        fill: new Fill({ color: '#ffffff' }),
      }),
      zIndex: emph ? 60 : 50,
    })
  }

  return new Style({
    image: new CircleStyle({
      radius: emph ? 7 : 4,
      fill: new Fill({ color: scoreColor(norm, 0.75) }),
      stroke: new Stroke({ color: '#ffffff', width: emph ? 1.2 : 0.6 }),
    }),
    zIndex: emph ? 40 : 10,
  })
}

const buildOverlay = () => ({
  id: 'redata-explorer-features',
  type: 'FeatureCollection',
  features: buildFeatures(),
  style: featureStyle,
  clusters: false,
})

const renderOverlayOnly = () => {
  mapRef.value?.updateOverlay(buildOverlay())
}

const featuresExtent = features => {
  if (!features.length) {
    return null
  }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const f of features) {
    const [x, y] = f.geometry.coordinates
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
  }
  // Pad ~5% so edge points aren't flush against the frame.
  const padX = (maxX - minX) * 0.05 || 400
  const padY = (maxY - minY) * 0.05 || 400
  return [minX - padX, minY - padY, maxX + padX, maxY + padY]
}

const renderMap = () => {
  if (!mapRef.value) {
    return
  }
  const overlay = buildOverlay()
  mapRef.value.updateOverlay(overlay)
  const extent = featuresExtent(overlay.features)
  if (extent) {
    mapRef.value.updateBbox(extent)
  }
}

// Table-row click → highlight, recenter on the point, and open its detail card.
const focusCandidate = candidate => {
  if (!candidate?.coords) {
    return
  }
  focusedId.value = candidate.id
  detailCandidate.value = candidate
  detailIsSubject.value = false
  renderOverlayOnly()
  const coord = toMapCoord(candidate.coords)
  mapRef.value?.updateBbox([coord[0] - 600, coord[1] - 600, coord[0] + 600, coord[1] + 600])
  nextTick(() => detailOverlay?.setPosition(coord))
}

// ─── Map feature hover (tooltip) + click (detail popup) ──────────────────────
const olMap = shallowRef(null)
const mapHost = ref(null)
const topSentinel = ref(null)
const detailEl = ref(null)
const mapTop = ref(112)
const hostSize = ref({ w: 0, h: 0 })

const hoverCandidate = shallowRef(null)
const hoverIsSubject = ref(false)
const hoverPos = ref({ x: 0, y: 0 })

const detailCandidate = shallowRef(null)
const detailIsSubject = ref(false)

let detailOverlay = null
let hoveredFeature = null

const featureAt = pixel => {
  let found = null
  olMap.value?.forEachFeatureAtPixel(
    pixel,
    feature => {
      if (feature.get('kind')) {
        found = feature
        return true
      }
      return false
    },
    { hitTolerance: 4 },
  )
  return found
}

const resolveFeature = feature => {
  if (feature.get('kind') === 'subject') {
    return { data: result.value?.subject, isSubject: true }
  }
  return { data: candidatesById.value.get(feature.get('id')), isSubject: false }
}

const handlePointerMove = evt => {
  if (evt.dragging || !olMap.value) {
    return
  }
  const feature = featureAt(evt.pixel)

  const target = olMap.value.getTargetElement()
  if (target) {
    target.style.cursor = feature ? 'pointer' : ''
  }

  // Per-feature hover highlight (cheap — only restyles the one feature).
  if (hoveredFeature && hoveredFeature !== feature) {
    hoveredFeature.set('hovered', false)
    hoveredFeature = null
  }

  if (!feature) {
    hoverCandidate.value = null
    return
  }

  if (hoveredFeature !== feature) {
    feature.set('hovered', true)
    hoveredFeature = feature
  }

  const { data, isSubject } = resolveFeature(feature)
  if (!data) {
    hoverCandidate.value = null
    return
  }
  hoverCandidate.value = data
  hoverIsSubject.value = isSubject
  hoverPos.value = { x: evt.pixel[0], y: evt.pixel[1] }
}

const handleMapClick = evt => {
  const feature = featureAt(evt.pixel)
  if (!feature) {
    closeDetail()
    return
  }
  const { data, isSubject } = resolveFeature(feature)
  if (!data) {
    return
  }
  detailCandidate.value = data
  detailIsSubject.value = isSubject
  if (!isSubject) {
    focusedId.value = data.id
    renderOverlayOnly()
  }
  // Anchor the popup to the feature (no recenter — keep the focal point in place).
  nextTick(() => detailOverlay?.setPosition(feature.getGeometry().getCoordinates()))
}

const closeDetail = () => {
  detailCandidate.value = null
  detailOverlay?.setPosition(undefined)
}

const onMapReady = map => {
  olMap.value = map
  if (detailEl.value) {
    detailOverlay = new Overlay({
      element: detailEl.value,
      positioning: 'bottom-center',
      offset: [0, -16],
      stopEvent: true,
    })
    map.addOverlay(detailOverlay)
  }
  map.on('pointermove', handlePointerMove)
  map.on('singleclick', handleMapClick)
  measureLayout()
  map.updateSize()
  // A deep-link run can land before the map is ready.
  if (result.value) {
    renderMap()
  }
}

// ─── Full-bleed layout sizing ────────────────────────────────────────────────
const measureLayout = () => {
  if (topSentinel.value) {
    mapTop.value = Math.max(0, Math.round(topSentinel.value.getBoundingClientRect().top))
  }
  if (mapHost.value) {
    hostSize.value = { w: mapHost.value.clientWidth, h: mapHost.value.clientHeight }
  }
}

const onResize = () => {
  measureLayout()
  olMap.value?.updateSize()
}

onMounted(async () => {
  await nextTick()
  measureLayout()
  await nextTick()
  olMap.value?.updateSize()
  window.addEventListener('resize', onResize)
  // "Open this valuation" deep link: show what live serves straight away.
  if (props.initialSubject?.type && props.initialSubject?.id) {
    runAnalysis()
  }
})

onBeforeUnmount(() => {
  runAbort?.abort()
  window.removeEventListener('resize', onResize)
  if (olMap.value) {
    olMap.value.un('pointermove', handlePointerMove)
    olMap.value.un('singleclick', handleMapClick)
  }
})

// ─── UI state + formatters ───────────────────────────────────────────────────
const tableOpen = ref(true)

const tooltipStyle = computed(() => {
  const { x, y } = hoverPos.value
  const flipX = x > hostSize.value.w - 240
  const flipY = y > hostSize.value.h - 160
  return {
    left: `${x}px`,
    top: `${y}px`,
    transform: `translate(${flipX ? 'calc(-100% - 16px)' : '16px'}, ${flipY ? 'calc(-100% - 16px)' : '16px'})`,
  }
})

function formatPrice(value) {
  if (value == null) {
    return '–'
  }
  return new Intl.NumberFormat('et-EE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// RED-262: weighted-average delta vs the model's raw prediction — the concise
// "how far does this pool mode pull the result" readout on the subject strip.
const waVsPrediction = value => {
  const pred = result.value?.subject?.predicted_value
  return value != null && pred ? ((value - pred) / pred) * 100 : null
}
const waDeltaText = value => {
  const d = waVsPrediction(value)
  return d == null ? '' : `${d > 0 ? '+' : ''}${d.toFixed(1)}%`
}
const waDeltaClass = value => {
  const d = waVsPrediction(value)
  return d == null || Math.abs(d) < 0.05
    ? 'text-gray-400'
    : d > 0
      ? 'text-green-600'
      : 'text-red-600'
}

const formatNumber = (value, digits = 2) => {
  if (value == null) {
    return '–'
  }
  return Number(value).toFixed(digits)
}

const formatMeters = value => {
  if (value == null) {
    return '–'
  }
  return value >= 1000 ? `${(value / 1000).toFixed(2)} km` : `${Math.round(value)} m`
}

const pricePerSqm = candidate => {
  if (!candidate.tehingusumma || !candidate.korteri_pind) {
    return null
  }
  return candidate.tehingusumma / candidate.korteri_pind
}


// The derived price estimates the SUBJECT's total value (comparable adjusted
// toward the target), so per-m² divides by the subject's area — every row's
// €/m² is then comparable to the others and to the subject's own prediction.
const subjectArea = computed(() => {
  const pind = Number(result.value?.subject_features?.hooneosa_pind)
  return pind > 0 ? pind : null
})

const derivedPricePerSqm = candidate => {
  if (!candidate.derived_price || !subjectArea.value) {
    return null
  }
  return candidate.derived_price / subjectArea.value
}

// The EBM's opinion of the candidate vs what it actually sold for — the same
// residual % the pool-gate filter operates on.
const ebmDeltaPct = candidate => {
  if (candidate.ebm_prediction == null || !candidate.tehingusumma) {
    return null
  }
  return ((candidate.ebm_prediction - candidate.tehingusumma) / candidate.tehingusumma) * 100
}

// Reference for the derived-price delta: the target's CatBoost raw prediction
// (the similarity price anchor); EBM raw prediction when no CatBoost ran.
const derivedReference = computed(
  () => subject.value?.catboost_prediction ?? subject.value?.predicted_value ?? null,
)

// How far this single-candidate derivation lands from the target's raw prediction.
const derivedDeltaPct = candidate => {
  if (candidate.derived_price == null || !derivedReference.value) {
    return null
  }
  return ((candidate.derived_price - derivedReference.value) / derivedReference.value) * 100
}

// ─── Subject feature inspector (RED-262 debugging surface) ──────────────────
const featuresOpen = ref(false)
const hasSubjectFeatures = computed(() => Object.keys(result.value?.subject_features ?? {}).length > 0)

const poolMeta = computed(() => result.value?.pool_meta || null)

// RED-262 "Automaatne": the API's dual-pass arbitration outcome (null unless the
// analysis ran with completeness_gate=auto) and the pool mode that ACTUALLY
// served — under auto the winner, otherwise the selector value. Drives the
// winner badge, the dotted active-mode underlines and the candidate cards' ●.
const autoGateResult = computed(() => result.value?.subject?.completeness_auto || null)
const resolvedPoolMode = computed(() =>
  completenessGate.value === 'auto'
    ? (autoGateResult.value?.mode_selected ?? 'off')
    : completenessGate.value,
)
const autoWinnerLabel = computed(() => {
  if (!autoGateResult.value) return null
  return autoGateResult.value.mode_selected === 'observed'
    ? 'Automaatne → Ainult rikastatud'
    : 'Automaatne → Rikastatud + AI'
})
const autoWinnerTitle = computed(() => {
  const a = autoGateResult.value
  if (!a) return null
  const d = mode => (a.delta?.[mode] != null ? formatPrice(a.delta[mode]) : '—')
  return (
    'Automaatne poolivalik: API hindas mõlemad poolid ja serveeris selle, mille kaalutud keskmine '
    + `on lähemal mudeli ennustusele (viigi korral ainult rikastatud). |Δ| rik+AI: ${d('imputed')}; `
    + `|Δ| ainult rik: ${d('observed')}.`
  )
})
const weightsUsed = computed(() => result.value?.weights_used || null)

// RED-262 comparable-pool policy: the resolved rule set the API built this pool
// under (trace.pool_policy — card or repo default, plus any request pool_gates
// override) and the fitted county band. Display-only: the explorer sends no
// pool_gates, so what shows here is exactly what production serves.
const poolPolicy = computed(() => poolMeta.value?.pool_policy || null)
const poolBand = computed(() => poolPolicy.value?.band || null)
const poolPolicyRules = computed(() => {
  const p = poolPolicy.value
  if (!p) {
    return []
  }
  const list = v => (Array.isArray(v) && v.length ? v.join(', ') : null)
  const band = p.price_band || {}
  return [
    {
      key: 'band',
      label: band.mode === 'absolute' ? 'hinnariba: absoluutne' : `hinnariba: maakonna suhtes (k ${band.k_mad ?? '–'}, min n ${band.min_n ?? '–'})`,
      on: true,
      tip: band.mode === 'absolute'
        ? 'Ainult absoluutne (legacy) hinnakast'
        : 'Legacy hinnakast (20 000–1 500 000 € / 500–8 000 €/m²) laieneb maakonna enda €/m² riba võrra (mediaan ± k·MAD log-skaalal), kunagi ei kitsene; välispiir jääb seinaks',
    },
    { key: 'seller', label: `müüja välja: ${list(p.exclude_seller_types) ?? '–'}`, on: !!list(p.exclude_seller_types), tip: 'myyja_liik koodid, mis poolist välja jäävad (M = kohalik omavalitsus, ~522 €/m² vs 2 392 turg)' },
    { key: 'iseloom', label: `iseloom välja: ${list(p.exclude_tehingu_iseloom) ?? '–'}`, on: !!list(p.exclude_tehingu_iseloom), tip: 'tehingu_iseloom koodid, mis poolist välja jäävad (EP = enampakkumine, SV = sundmüük)' },
    { key: 'vat', label: p.vat === 'exclude' ? 'KM-ga tehingud välja' : 'KM-ga tehingud alles', on: p.vat === 'exclude', tip: 'sisaldab_km read (uusarenduste arendajamüügid) — vaikimisi alles, sest serveeritav väärtus on ostja hind' },
    { key: 'dedupe', label: 'sama korteri topelt', on: !!p.same_unit_dedupe, tip: 'Reegel B: sama lahiaadress + sama hind = topeltregistreering, alles jääb uusim' },
    { key: 'korter', label: 'korter=0 välja', on: !!p.korter, tip: 'Mitte-korteri read (andmestiku korter = 0) poolist välja; teadmata arv (null) läbib' },
  ]
})
const poolPolicyOverrides = computed(() => poolPolicy.value?.overrides || [])
const formatM2 = value => (value == null ? '–' : `${Math.round(value).toLocaleString('et-EE')} €/m²`)
const poolBandTitle = computed(() => {
  const b = poolBand.value
  if (!b) {
    return ''
  }
  const lines = [
    `Kehtiv hinnakast, mille sees kandidaadid on: ${formatM2(b.lo)} – ${formatM2(b.hi)}, ${formatPrice(b.price_lo)} – ${formatPrice(b.price_hi)}.`,
    b.legacy ? `Legacy (üleriigiline) riba: ${formatM2(b.legacy.lo)} – ${formatM2(b.legacy.hi)}.` : null,
    b.relative
      ? `Maakonna (${b.county}) sobitatud riba n=${b.n} rea pealt: ${formatM2(b.relative.lo)} – ${formatM2(b.relative.hi)} — laiendab legacy kasti ainult sealt, kus on legacy'st väljas.`
      : `Maakonna riba ei sobitatud (${b.county ? `n=${b.n} < min n` : 'maakond teadmata'}) — kehtib legacy kast.`,
  ]
  return lines.filter(Boolean).join('\n')
})
const reference = computed(() => result.value?.reference || null)
const subject = computed(() => result.value?.subject || null)

// ─── Top-20 pool statistics (servability signals) ───────────────────────────
const poolStats = computed(() => result.value?.pool_stats || null)

// The calibrated serve/borderline/no_serve verdict (ServabilityGate) — absent
// until a servability config is promoted, so the chip renders conditionally.
const servability = computed(() => result.value?.servability || null)
const servabilityTag = computed(() => {
  const map = {
    serve: { severity: 'success', label: 'Serveeritav' },
    borderline: { severity: 'warn', label: 'Piiripealne' },
    no_serve: { severity: 'danger', label: 'Ei serveeri' },
  }
  return map[servability.value?.verdict] || null
})
const servabilityTitle = computed(() => {
  if (!servability.value) {
    return ''
  }
  const lines = servability.value.checks.map(c => {
    const value = c.value != null ? formatNumber(c.value, 2) : 'puudub'
    const serve = c.serve_below != null ? formatNumber(c.serve_below, 2) : '—'
    const block = c.block_above != null ? formatNumber(c.block_above, 2) : '—'
    return `${c.feature}: ${value} (serveeri < ${serve}, blokeeri > ${block}) → ${c.verdict}`
  })
  return `Kalibreeritud serveeritavuse otsus (config #${servability.value.config_id}):\n${lines.join('\n')}`
})
const scoreHistMax = computed(() =>
  Math.max(1, ...(poolStats.value?.score?.histogram?.counts || [1])),
)
const scoreHistBinTitle = (i, count) => {
  const hist = poolStats.value?.score?.histogram
  if (!hist) {
    return ''
  }
  const from = hist.start + i * hist.bin_width
  return `${formatNumber(from, 2)}–${formatNumber(from + hist.bin_width, 2)}: ${count}`
}
</script>

<template>
  <div>
    <!-- 0-height anchor: marks the y just below the admin tabs so the fixed map
         host can sit flush beneath them while the tabs stay visible. -->
    <div ref="topSentinel" class="h-0"></div>

    <div
      ref="mapHost"
      class="fixed left-0 right-0 bottom-0 z-0 overflow-hidden"
      :style="{ top: `${mapTop}px` }"
    >
      <MapComponent
        ref="mapRef"
        class="w-full h-full"
        :center="mapview.center"
        :zoom="7"
        :min-zoom="0"
        :max-zoom="16"
        projection="EPSG:3301"
        :base-layers="mapview.baseLayers"
        :base-layer="mapview.baseLayer"
        :zoom-control="false"
        @ready="onMapReady"
      />

      <!-- Empty state -->
      <div
        v-if="!result"
        class="absolute inset-0 flex items-center justify-center text-gray-500 text-sm pointer-events-none"
      >
        <span class="bg-white/80 backdrop-blur-xs rounded-md px-3 py-1.5">
          Käivita analüüs, et näha kandidaatide punktipilve
        </span>
      </div>

      <!-- Controls (floating, translucent, top-left). Auto-sizes to its content
           and only scrolls as a safety net on very short viewports. -->
      <div
        class="absolute top-4 left-4 w-[360px] max-h-[calc(100%-2rem)] bg-white/90 backdrop-blur-xs rounded-lg shadow-lg p-3 space-y-3 overflow-y-auto z-20"
      >
        <h2 class="text-sm font-semibold text-gray-800">Mudeli ülevaade</h2>

        <div v-if="errorMessage" class="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-sm text-sm">
          {{ errorMessage }}
        </div>

        <div class="space-y-2">
          <div class="flex items-center gap-2">
            <span class="w-24 shrink-0 text-xs text-gray-600">Objekti tüüp</span>
            <Select
              v-model="subjectType"
              :options="subjectTypeOptions"
              option-label="label"
              option-value="value"
              size="small"
              class="flex-1 min-w-0"
              @change="subjectId = null"
            />
          </div>
          <div class="flex items-center gap-2">
            <span class="w-24 shrink-0 text-xs text-gray-600">Objekt</span>
            <Select
              v-model="subjectId"
              :options="subjectOptions"
              option-label="label"
              option-value="value"
              filter
              :filter-fields="['label', 'meta']"
              placeholder="Vali objekt"
              size="small"
              class="flex-1 min-w-0"
              :empty-message="'Objekte ei leitud'"
            >
              <template #option="slotProps">
                <div class="flex flex-col gap-0.5 py-0.5">
                  <div class="font-medium">{{ slotProps.option.label }}</div>
                  <div v-if="slotProps.option.meta || slotProps.option.created_at" class="flex gap-2 text-xs text-gray-500">
                    <span v-if="slotProps.option.meta" class="font-mono">{{ slotProps.option.meta }}</span>
                    <span v-if="slotProps.option.created_at">
                      <span aria-hidden="true">·</span>
                      {{ formatDate(String(slotProps.option.created_at).slice(0, 10)) }}
                    </span>
                  </div>
                </div>
              </template>
            </Select>
          </div>
          <div class="flex items-center gap-2">
            <span
              class="w-24 shrink-0 text-xs inline-flex items-center gap-1"
              :class="labelClass('model_type')"
              :title="liveTitle('model_type')"
            >
              Mudel<i v-if="isOverridden('model_type')" class="pi pi-circle-fill text-[6px] text-orange-500" />
            </span>
            <ModelSelector
              v-model="modelType"
              :models="modelsList"
              type="ebm"
              placeholder="Vali mudel"
              size="small"
              class="flex-1 min-w-0!"
            />
            <Button
              v-tooltip.left="'Uuenda mudelite nimekirja (laeb ML API-sse värskelt lisatud kandidaadid ilma serverit taaskäivitamata)'"
              icon="pi pi-refresh"
              severity="secondary"
              outlined
              size="small"
              :loading="refreshingModels"
              class="w-auto! shrink-0"
              @click="refreshModels"
            />
          </div>
        </div>

        <SimilarityWeightControls
          v-model="config"
          :bounds="weightBounds"
          :catboost-models="catboostModels"
          :changed="overriddenKeys"
          :live="base"
        />

        <div class="flex items-center gap-2">
          <span class="w-24 shrink-0 text-xs text-gray-600">Pilve suurus</span>
          <Select
            v-model="candidatePoolLimit"
            :options="poolLimitOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="flex-1 min-w-0"
          />
        </div>

        <div class="flex items-center gap-2">
          <span
            class="w-24 shrink-0 text-xs inline-flex items-center gap-1"
            :class="labelClass('rerank_mode')"
            :title="liveTitle('rerank_mode')"
          >
            <i v-if="isOverridden('rerank_mode')" class="pi pi-circle-fill text-[6px] text-orange-500" />
            Järjestus
            <i
              class="pi pi-info-circle text-gray-400 cursor-help"
              v-tooltip.top="'Kuidas võrdlustehingud valitakse: L1 sarnasuskoor või kaheastmeline tuletusvea mudel — L1 valib parimad N kandidaati (lühinimekiri), mudel järjestab need ümber ennustatud tuletusvea järgi. Ilma DERIV mudelita langeb API tagasi L1-le.'"
            />
          </span>
          <Select
            v-model="rerankMode"
            :options="rerankOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="flex-1 min-w-0"
          />
          <InputNumber
            v-if="rerankMode === 'derivation_error'"
            v-model="rerankShortlist"
            :min="5"
            :max="500"
            size="small"
            class="w-20! shrink-0 rounded-md"
            :class="{ 'ring-1 ring-orange-400': isOverridden('rerank_shortlist') }"
            :title="liveTitle('rerank_shortlist')"
            :input-class="'w-full!'"
            v-tooltip.top="'Lühinimekirja suurus: mitu parimat L1 kandidaati on ümberjärjestusele kõlblikud'"
          />
        </div>

        <div v-if="rerankMode === 'derivation_error'" class="flex items-center gap-1.5">
          <span
            class="w-24 shrink-0 text-xs inline-flex items-center gap-1"
            :class="labelClass('rerank_rank_weights')"
            :title="liveTitle('rerank_rank_weights')"
          >
            <i v-if="isOverridden('rerank_rank_weights')" class="pi pi-circle-fill text-[6px] text-orange-500" />
            Järjestuskaalud
            <i
              class="pi pi-info-circle text-gray-400 cursor-help"
              v-tooltip.top="'Lõppjärjestus = kaalutud keskmine komponentide JÄRKUDEST lühinimekirjas (skaalavaba). viga=mudeli eelistus, sarn=L1, geo=kaugus, koh=kohanduse suurus. Vaikimisi 1/0/0/0 = puhas mudel.'"
            />
          </span>
          <div v-for="f in rerankWeightFields" :key="f.key" class="flex items-center gap-0.5">
            <span class="text-[10px] text-gray-400" :title="f.tip">{{ f.label }}</span>
            <InputNumber
              v-model="rerankWeights[f.key]"
              :min="0"
              :max="1"
              :step="0.1"
              :min-fraction-digits="0"
              :max-fraction-digits="2"
              size="small"
              class="w-14!"
              :input-class="'w-full! px-1!'"
            />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <span
            class="w-24 shrink-0 text-xs inline-flex items-center gap-1"
            :class="labelClass('completeness_gate')"
            :title="liveTitle('completeness_gate')"
          >
            <i v-if="isOverridden('completeness_gate')" class="pi pi-circle-fill text-[6px] text-orange-500" />
            Andmete täielikkus
            <i
              class="pi pi-info-circle text-gray-400 cursor-help"
              v-tooltip.top="'Kandidaatide filter viie seisukorravälja (hoone/ruumide seisukord, parkimine, panipaik, rõdu) järgi: Rikastatud + AI = väärtus olemas või AI poolt tuletatud; Ainult rikastatud = kõik viis käsitsi rikastatud; Automaatne = API hindab mõlemad poolid ja serveerib selle, mille kaalutud keskmine on lähemal mudeli ennustusele (viigi korral ainult rikastatud). Serveripoolne astmeline tagasilangemine hoiab õhukese turu puhul valiku töökorras (vt „tier“ meetrikat).'"
            />
          </span>
          <Select
            v-model="completenessGate"
            :options="completenessGateOptions"
            option-label="label"
            option-value="value"
            size="small"
            class="flex-1 min-w-0"
          />
        </div>

        <div class="flex items-center gap-2">
          <span
            class="w-24 shrink-0 text-xs inline-flex items-center gap-1"
            :class="labelClass('impute_comparable_features')"
            :title="liveTitle('impute_comparable_features')"
          >
            <i v-if="isOverridden('impute_comparable_features')" class="pi pi-circle-fill text-[6px] text-orange-500" />
            AI-tunnused
            <i
              class="pi pi-info-circle text-gray-400 cursor-help"
              v-tooltip.top="'Võrdlustehingute AI poolt tuletatud seisukorraväärtused lähevad ka mudeli tunnustesse, nii et kohandused arvestavad neid. Live väärtus tuleb serveeriva mudeli seadetest.'"
            />
          </span>
          <ToggleSwitch v-model="imputeComparableFeatures" inputId="impute-comparable-features" class="scale-75" />
        </div>

        <div class="flex items-center gap-2">
          <span
            class="w-24 shrink-0 text-xs inline-flex items-center gap-1"
            :class="labelClass('pool_residual_gate')"
            :title="liveTitle('pool_residual_gate')"
          >
            <i v-if="isOverridden('pool_residual_gate')" class="pi pi-circle-fill text-[6px] text-orange-500" />
            EBM-jäägi piir
            <i
              class="pi pi-info-circle text-gray-400 cursor-help"
              v-tooltip.top="'Filtreerib kandidaadid, mille müügihind erineb EBM toorennustusest üle nende piiride (%). Tühi = filter väljas. Algväärtus on live väärtus (serveeriva mudeli seadetest). Sama jääk on näha „EBM hind“ veeru protsendina.'"
            />
          </span>
          <InputNumber
            v-model="residualGateLower"
            placeholder="alumine"
            :min="-95"
            :max="0"
            size="small"
            class="flex-1 min-w-0!"
            :input-class="'w-full!'"
          />
          <InputNumber
            v-model="residualGateUpper"
            placeholder="ülemine"
            :min="0"
            :max="300"
            size="small"
            class="flex-1 min-w-0!"
            :input-class="'w-full!'"
          />
        </div>

        <div class="flex items-center gap-2 pt-2 border-t">
          <ToggleSwitch v-model="useSegmentConfig" inputId="use-segment-config" class="scale-75" />
          <label
            for="use-segment-config"
            class="text-xs inline-flex items-center gap-1 cursor-pointer"
            :class="isOverridden('use_segment_config') ? 'text-orange-600 font-medium' : 'text-gray-500'"
            :title="liveTitle('use_segment_config')"
          >
            <i v-if="isOverridden('use_segment_config')" class="pi pi-circle-fill text-[6px] text-orange-500" />
            Segmendikonfiguratsioon
            <i
              class="pi pi-info-circle text-gray-400 cursor-help"
              v-tooltip.top="'Live suunab subjekti alati läbi aktiivse segmentatsiooni (RED-262) — segmendi häälestatud kaalud kirjutavad serveeriva mudeli kaalud üle (rakendunud segment kuvatakse tulemuses); muudetud liugurid võidavad segmendi. Väljas = puhtad liuguri kaalud. Arvestab „segment-configs“ funktsioonilippu.'"
            />
          </label>
        </div>
        <div class="pt-2 border-t space-y-1.5">
          <div class="flex flex-wrap items-center gap-2">
            <Button
              label="Käivita analüüs"
              icon="pi pi-play"
              size="small"
              :loading="loading && runSource === 'run'"
              :disabled="!canRun"
              @click="runAnalysis()"
            />
            <Button
              label="Taasta live seaded"
              icon="pi pi-undo"
              size="small"
              severity="secondary"
              outlined
              :loading="loading && runSource === 'reset'"
              :disabled="!overriddenFields.length"
              v-tooltip.top="'Kõik juhtnupud tagasi live väärtustele (serveeritav mudel, selle kalibreeritud kaalud, väravad, segment) ja käivita uuesti'"
              @click="restoreLive"
            />
            <span
              class="ml-auto text-[11px] px-2 py-0.5 rounded-full font-medium cursor-help shrink-0"
              :class="overriddenFields.length ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-700'"
              v-tooltip.top="
                overriddenFields.length
                  ? 'Live peale pannakse: ' + overrideLabel
                  : 'Päring on täpselt see, mille live kiirhindamine selle hindamise omaniku jaoks saadaks'
              "
            >
              {{ overriddenFields.length ? `Muudetud (${overriddenFields.length})` : 'Live seaded' }}
            </span>
          </div>
          <div v-if="overriddenFields.length" class="text-[11px] text-orange-600">
            Muudetud: {{ overrideLabel }}
          </div>
          <!-- Space reserved: the hint coming and going must not move the controls. -->
          <div
            class="text-[11px] text-gray-500 flex items-center gap-1 min-h-4"
            :class="{ invisible: !runHint }"
            :title="runHint?.title ?? undefined"
            data-testid="explorer-run-hint"
          >
            <i class="pi pi-info-circle text-[10px] shrink-0" />
            <span class="truncate min-w-0">{{ runHint?.text ?? 'Muudatused on käivitamata' }}</span>
          </div>
        </div>
      </div>

      <!-- Subject summary (floating, translucent, top-right) -->
      <div
        v-if="subject"
        class="absolute top-4 right-4 max-w-[440px] max-h-[calc(100%-2rem)] overflow-y-auto bg-white/90 backdrop-blur-xs rounded-lg shadow-lg p-3 z-20"
      >
        <!-- RED-356: the live result page shows the direct price here, not the comparables below. -->
        <Message
          v-if="liveAnswerNotice"
          :severity="liveAnswerNotice.severity"
          size="small"
          :icon="liveAnswerNotice.severity === 'warn' ? 'pi pi-exclamation-triangle' : 'pi pi-info-circle'"
          class="mb-2"
        >
          {{ liveAnswerNotice.text }}
        </Message>
        <div class="flex items-center flex-wrap gap-x-5 gap-y-2">
          <div class="min-w-0">
            <div class="text-[11px] text-gray-400 leading-none">Objekt</div>
            <div class="font-semibold text-gray-800 truncate">{{ subject.label }}</div>
          </div>
          <Tag
            v-if="servabilityTag"
            :severity="servabilityTag.severity"
            :value="servabilityTag.label"
            class="cursor-help"
            :title="servabilityTitle"
          />
          <div v-if="subject.manual_valuation != null">
            <div class="text-[11px] text-gray-400 leading-none">Käsitsi hinnatud</div>
            <div class="font-semibold text-sm">{{ formatPrice(subject.manual_valuation) }}</div>
          </div>
          <div>
            <div class="text-[11px] text-gray-400 leading-none">Mudeli ennustus</div>
            <div class="font-semibold text-sm">{{ formatPrice(subject.predicted_value) }}</div>
          </div>
          <div v-if="subject.catboost_prediction != null && subject.catboost_model !== 'ebm'" :title="subject.catboost_model">
            <div class="text-[11px] text-gray-400 leading-none">CatBoost prognoos</div>
            <div class="font-semibold text-sm">{{ formatPrice(subject.catboost_prediction) }}</div>
          </div>
          <div>
            <div class="text-[11px] text-gray-400 leading-none">
              Kaalutud keskmine{{ subject.weighted_average_by_pool_mode ? ' (kõik)' : '' }}
              <Tag
                v-if="autoWinnerLabel"
                severity="info"
                :value="autoWinnerLabel"
                class="ml-1 cursor-help text-[10px]! py-0! px-1.5! align-middle"
                :title="autoWinnerTitle"
              />
            </div>
            <div class="font-semibold text-sm" :class="{ 'underline decoration-dotted': resolvedPoolMode === 'off' }">
              {{ formatPrice(subject.weighted_average_by_pool_mode?.off ?? subject.weighted_average) }}<span
                v-if="waVsPrediction(subject.weighted_average_by_pool_mode?.off ?? subject.weighted_average) != null"
                class="text-xs ml-1 font-normal"
                :class="waDeltaClass(subject.weighted_average_by_pool_mode?.off ?? subject.weighted_average)"
                title="Erinevus mudeli ennustusest"
              >{{ waDeltaText(subject.weighted_average_by_pool_mode?.off ?? subject.weighted_average) }}</span>
            </div>
          </div>
          <div v-if="subject.weighted_average_by_pool_mode" :title="'Kaalutud keskmine, kui kandidaadipool piirata rikastatud + AI-imputeeritud seisukorraväljadega. Punktiir = aktiivne poolirežiim; % = erinevus mudeli ennustusest.'">
            <div class="text-[11px] text-gray-400 leading-none">Kaalutud keskmine (rik+imputed)</div>
            <div class="font-semibold text-sm" :class="{ 'underline decoration-dotted': resolvedPoolMode === 'imputed' }">
              {{ formatPrice(subject.weighted_average_by_pool_mode.imputed) }}<span
                v-if="waVsPrediction(subject.weighted_average_by_pool_mode.imputed) != null"
                class="text-xs ml-1 font-normal"
                :class="waDeltaClass(subject.weighted_average_by_pool_mode.imputed)"
              >{{ waDeltaText(subject.weighted_average_by_pool_mode.imputed) }}</span>
            </div>
          </div>
          <div v-if="subject.weighted_average_by_pool_mode" :title="'Kaalutud keskmine, kui kandidaadipool piirata ainult käsitsi rikastatud kandidaatidega. Punktiir = aktiivne poolirežiim; % = erinevus mudeli ennustusest.'">
            <div class="text-[11px] text-gray-400 leading-none">Kaalutud keskmine (rikastatud)</div>
            <div class="font-semibold text-sm" :class="{ 'underline decoration-dotted': resolvedPoolMode === 'observed' }">
              {{ formatPrice(subject.weighted_average_by_pool_mode.observed) }}<span
                v-if="waVsPrediction(subject.weighted_average_by_pool_mode.observed) != null"
                class="text-xs ml-1 font-normal"
                :class="waDeltaClass(subject.weighted_average_by_pool_mode.observed)"
              >{{ waDeltaText(subject.weighted_average_by_pool_mode.observed) }}</span>
            </div>
          </div>
          <div v-if="poolMeta">
            <div class="text-[11px] text-gray-400 leading-none">Kandidaate</div>
            <div class="font-semibold text-sm">
              {{ poolMeta.returned }}<span v-if="poolMeta.truncated" class="text-gray-400"> / {{ poolMeta.total_evaluated }}</span>
            </div>
            <div v-if="poolMeta.pool_guard_gated > 0" class="text-[11px] text-rose-600" :title="'Välispiir (treeningu hinnavahemik 5 000–2 256 000 € / 30–20 000 €/m²) eemaldas need kandidaadid'">
              −{{ poolMeta.pool_guard_gated }} välispiiriga
            </div>
            <div v-if="poolMeta.pool_band_gated > 0" class="text-[11px] text-rose-500" :title="'Hinnariba (legacy kast, mida maakonna riba võib ainult laiendada) eemaldas need kandidaadid'">
              −{{ poolMeta.pool_band_gated }} hinnaribaga
            </div>
            <div v-else-if="poolMeta.pool_guard_gated == null && poolMeta.pool_price_gated > 0" class="text-[11px] text-rose-500" :title="'Hinnavärav (vanem API: ühe sammuna) eemaldas need kandidaadid'">
              −{{ poolMeta.pool_price_gated }} hinnaväravaga
            </div>
            <div v-if="poolMeta.pool_seller_gated > 0" class="text-[11px] text-violet-600" :title="'Välistatud müüja liik (myyja_liik, vaikimisi M = kohalik omavalitsus) eemaldas need kandidaadid'">
              −{{ poolMeta.pool_seller_gated }} müüja liigiga
            </div>
            <div v-if="poolMeta.pool_iseloom_gated > 0" class="text-[11px] text-violet-600" :title="'Välistatud tehingu iseloom (tehingu_iseloom, vaikimisi EP = enampakkumine, SV = sundmüük) eemaldas need kandidaadid'">
              −{{ poolMeta.pool_iseloom_gated }} tehingu iseloomuga
            </div>
            <div v-if="poolMeta.pool_vat_gated > 0" class="text-[11px] text-violet-600" :title="'KM-ga tehingud (sisaldab_km) välistati poliitika järgi'">
              −{{ poolMeta.pool_vat_gated }} KM-ga
            </div>
            <div v-if="poolMeta.pool_korter_gated > 0" class="text-[11px] text-violet-600" :title="'Mitte-korteri read (korter = 0) välistati poliitika järgi'">
              −{{ poolMeta.pool_korter_gated }} mitte-korteriga
            </div>
            <div v-if="poolMeta.pool_residual_gated > 0" class="text-[11px] text-orange-500" :title="'EBM-jäägi värav eemaldas need kandidaadid enne järjestamist'">
              −{{ poolMeta.pool_residual_gated }} jäägiväravaga
            </div>
            <div v-if="poolMeta.pool_duplicate_gated > 0" class="text-[11px] text-amber-600" :title="'Topeltkirjed (sama objekt, sama hind, kpv ≤ 15 p vahet) — alles jäi uusim koopia'">
              −{{ poolMeta.pool_duplicate_gated }} duplikaati
            </div>
            <div v-if="poolMeta.pool_completeness_gated > 0" class="text-[11px] text-sky-600" :title="'Täielikkuse värav eemaldas kandidaadid, mille seisukorraväljad pole kuvatavad. Tier näitab, milline aste tegelikult rakendus (astmeline tagasilangemine õhukesel turul).'">
              −{{ poolMeta.pool_completeness_gated }} täielikkusega ({{ poolMeta.completeness_gate_tier }})
            </div>
          </div>
          <div v-if="poolMeta && poolMeta.rerank_mode_used">
            <div class="text-[11px] text-gray-400 leading-none">Järjestus</div>
            <div class="font-semibold text-sm">
              {{ poolMeta.rerank_mode_used === 'derivation_error' ? 'Tuletusviga' : 'L1' }}
            </div>
          </div>
          <div v-if="poolMeta && poolMeta.corrected_weighted_average != null" :title="'Kallutusparandusega kaalutud keskmine: tootmiskaalud × (tuletatud hind / (1 + ennustatud viga)) valitud võrdluste üle. Eksperimentaalne.'">
            <div class="text-[11px] text-gray-400 leading-none">Korr. keskmine</div>
            <div class="font-semibold text-sm">{{ formatPrice(poolMeta.corrected_weighted_average) }}</div>
          </div>
          <div
            v-if="poolMeta && poolMeta.confidence"
            :title="`Empiiriline usaldusvahemik mudeli enda kalibratsioonist (skoor ${poolMeta.confidence.score}, n=${poolMeta.confidence.n_calibration}): pooltel juhtudel jääb viga alla p50, 90% juhtudest alla p90.`"
          >
            <div class="text-[11px] text-gray-400 leading-none">Viga p50 / p90</div>
            <div class="font-semibold text-sm">
              ±{{ (poolMeta.confidence.ape_p50 * 100).toFixed(1) }}% / ±{{ (poolMeta.confidence.ape_p90 * 100).toFixed(1) }}%
            </div>
          </div>
          <Button
            v-if="hasSubjectFeatures"
            label="Tunnused"
            icon="pi pi-list"
            size="small"
            severity="secondary"
            outlined
            class="w-auto!"
            @click="featuresOpen = true"
          />
        </div>
        <!-- RED-356: what live serves for this valuation and why — owner, route, model, flags, gate. -->
        <ExplorerServedContext
          v-if="served"
          class="mt-2 pt-2 border-t"
          :served="served"
          :is-test-case="resultSubjectType === 'test_case'"
          :rebuilding="loading && runSource === 'fresh'"
          :field-labels="fieldLabels"
          @rebuild-features="rebuildFeatures"
        />
        <div v-if="subject.building_part" class="mt-2 pt-2 border-t text-[11px]">
          <Tag
            v-if="subject.building_part.source === 'building-level'"
            severity="danger"
            icon="pi pi-exclamation-triangle"
            value="Hooneosa tuvastamata — kasutati hoone tasandi tunnuseid (pind = kogu hoone SNP)"
          />
          <span v-else class="font-mono text-gray-400">
            hooneosa {{ subject.building_part.id }}
            <span v-if="subject.building_part.source === 'detected-from-address'">· tuvastatud aadressi järgi</span>
          </span>
        </div>
        <div v-if="reference" class="mt-2 pt-2 border-t text-[11px] text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
          <span>Algne ennustus ({{ reference.model_type }}): <b class="text-gray-700">{{ formatPrice(reference.predicted_value) }}</b></span>
          <span>Algne võrdluskeskmine: <b class="text-gray-700">{{ formatPrice(reference.comparable_average) }}</b></span>
          <span>Algselt {{ reference.n_comparables }} võrdlust</span>
        </div>
        <div v-if="appliedSegment" class="mt-2 pt-2 border-t text-[11px]">
          <Tag :value="`Segment #${appliedSegment.leaf_id} kaalud rakendatud`" severity="info" />
          <span class="ml-2 font-mono text-gray-400">
            zdist {{ appliedSegment.overrides.zdist_weight }} · price {{ appliedSegment.overrides.price_diff_weight }} · rik {{ appliedSegment.overrides.rikastatus_weight }}<template v-if="appliedSegment.overrides.location_proximity_km"> · prox {{ appliedSegment.overrides.location_proximity_km }} km</template>
          </span>
        </div>
        <div v-if="weightsUsed" class="mt-1 font-mono text-[11px] text-gray-400">
          zdist {{ weightsUsed.zdist_weight }} · price {{ weightsUsed.price_diff_weight }} · rik {{ weightsUsed.rikastatus_weight }}
        </div>
        <!-- RED-262 comparable-pool policy: which rule set built this pool and the
             county band it resolved to. Read-only — the explorer never sends pool_gates. -->
        <div v-if="poolPolicy" class="mt-2 pt-2 border-t text-[11px]">
          <div class="flex items-center flex-wrap gap-x-2 gap-y-1">
            <span class="text-gray-400">Poolipoliitika</span>
            <Tag
              :value="poolPolicy.source === 'card' ? 'mudelikaart' : 'vaikimisi (KOM)'"
              severity="secondary"
              class="text-[10px]! py-0! px-1.5! cursor-help"
              :title="poolPolicy.source === 'card' ? 'Reeglid tulevad serveeriva mudeli kaardilt (serving.pool)' : 'Kaardil poolireegleid pole — kehtib redata-api KOM vaikepoliitika'"
            />
            <Tag
              v-if="poolPolicyOverrides.length"
              :value="`päring kirjutas üle: ${poolPolicyOverrides.join(', ')}`"
              severity="warn"
              class="text-[10px]! py-0! px-1.5!"
            />
          </div>
          <div v-if="poolBand" class="mt-1 font-mono text-gray-500 cursor-help" :title="poolBandTitle">
            {{ poolBand.county || 'maakond teadmata' }} · n {{ poolBand.n ?? '–' }} ·
            {{ formatM2(poolBand.lo) }} – {{ formatM2(poolBand.hi) }} ·
            {{ formatPrice(poolBand.price_lo) }} – {{ formatPrice(poolBand.price_hi) }}
            <span v-if="!poolBand.relative" class="text-gray-400">(legacy kast)</span>
          </div>
          <div class="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span
              v-for="rule in poolPolicyRules"
              :key="rule.key"
              class="inline-flex items-center gap-1 cursor-help"
              :class="rule.on ? 'text-gray-600' : 'text-gray-400 line-through'"
              :title="rule.tip"
            >
              <i class="pi text-[10px]" :class="rule.on ? 'pi-check text-green-600' : 'pi-minus'"></i>{{ rule.label }}
            </span>
          </div>
        </div>
      </div>

      <!-- Hover tooltip (cursor-anchored summary) -->
      <div
        v-if="hoverCandidate"
        class="absolute z-30 pointer-events-none bg-white/95 backdrop-blur-xs rounded-md shadow-lg border border-gray-200 px-3 py-2"
        :style="tooltipStyle"
      >
        <CandidateInfoCard :candidate="hoverCandidate" :subject="hoverIsSubject" :reference-price="derivedReference" :active-pool-mode="resolvedPoolMode" compact />
      </div>

      <!-- Click detail popup (OpenLayers Overlay element — OL positions it and
           tracks the feature on pan/zoom; Vue patches the inner content). -->
      <div ref="detailEl">
        <div
          v-if="detailCandidate"
          class="relative bg-white/95 backdrop-blur-xs rounded-lg shadow-xl border border-gray-200 p-3 pr-7"
        >
          <button
            class="absolute top-1.5 right-1.5 text-gray-400 hover:text-gray-700"
            aria-label="Sulge"
            @click="closeDetail"
          >
            <i class="pi pi-times text-xs"></i>
          </button>
          <CandidateInfoCard :candidate="detailCandidate" :subject="detailIsSubject" :reference-price="derivedReference" :active-pool-mode="resolvedPoolMode" />
        </div>
      </div>

      <!-- Results: bottom collapsible translucent panel. Docked to the right of
           the controls rail so it never shrinks / overlaps the controls. -->
      <div
        v-if="result"
        class="absolute bottom-0 left-[392px] right-0 bg-white/90 backdrop-blur-xs border border-gray-200 rounded-t-lg shadow-lg z-20"
      >
        <div
          class="flex items-center gap-3 px-4 py-2 cursor-pointer select-none"
          @click="tableOpen = !tableOpen"
        >
          <span class="font-medium text-gray-700 text-sm whitespace-nowrap">
            Kandidaadid ({{ result.candidates.length }})
          </span>
          <div class="hidden md:flex items-center gap-3 text-xs text-gray-500 flex-wrap">
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-full" style="background:#DB3848"></span> Objekt</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-full border-2 border-blue-700" style="background:hsl(120,70%,48%)"></span> Valitud võrdlus</span>
            <span class="flex items-center gap-1"><span class="inline-block w-2.5 h-2.5 rounded-full" style="background:hsl(120,70%,48%)"></span> roheline = parim sobivus</span>
            <span class="flex items-center gap-1"><span class="inline-block w-2.5 h-2.5 rounded-full" style="background:hsl(0,70%,48%)"></span> punane = kehvem</span>
          </div>
          <i class="pi ml-auto text-gray-500" :class="tableOpen ? 'pi-chevron-down' : 'pi-chevron-up'"></i>
        </div>
        <div v-show="tableOpen" class="max-h-[40vh] overflow-auto px-2 pb-2">
          <!-- Top-20 pool statistics: the servability signals over the best-ranked
               candidates — derived-price dispersion, koondskoor distribution,
               Σ|derivation error|. Hidden entirely for old/poolless payloads. -->
          <div
            v-if="poolStats"
            class="flex items-end flex-wrap gap-x-6 gap-y-2 px-2 pt-1 pb-2 border-b border-gray-100"
          >
            <span class="text-[11px] text-gray-400 self-center">Top {{ poolStats.top_n }}</span>
            <div
              v-if="poolStats.derived_price && poolStats.derived_price.std_dev != null"
              :title="`Tuletatud hindade hajuvus top ${poolStats.top_n} kandidaadi üle (n=${poolStats.derived_price.n}): valimi standardhälve ja variatsioonikordaja. Väiksem = võrdlustehingud on subjekti väärtuses ühel meelel. Vahemik ${formatPrice(poolStats.derived_price.min)}–${formatPrice(poolStats.derived_price.max)}.`"
            >
              <div class="text-[11px] text-gray-400 leading-none">Tuletatud hinna hajuvus</div>
              <div class="font-semibold text-sm">
                σ {{ formatPrice(poolStats.derived_price.std_dev) }}
                <span v-if="poolStats.derived_price.cv_pct != null" class="text-xs text-gray-500">
                  ({{ formatNumber(poolStats.derived_price.cv_pct, 1) }} %)
                </span>
              </div>
            </div>
            <div
              v-if="poolStats.score"
              :title="`Koondskoori jaotus top ${poolStats.top_n} üle (n=${poolStats.score.n}): min / mediaan / max, kvartiilid ${formatNumber(poolStats.score.q1, 2)}–${formatNumber(poolStats.score.q3, 2)}. Madal ja tihe = tugev võrdluspool.`"
            >
              <div class="text-[11px] text-gray-400 leading-none">Koondskoor min / med / max</div>
              <div class="font-semibold text-sm">
                {{ formatNumber(poolStats.score.min, 2) }} /
                {{ formatNumber(poolStats.score.median, 2) }} /
                {{ formatNumber(poolStats.score.max, 2) }}
                <span class="text-xs text-gray-400">IQR {{ formatNumber(poolStats.score.iqr, 2) }}</span>
              </div>
            </div>
            <div
              v-if="poolStats.score?.histogram"
              class="flex items-end gap-px h-8 w-28"
              :title="`Koondskoori histogramm (10 vahemikku, ${formatNumber(poolStats.score.min, 2)}–${formatNumber(poolStats.score.max, 2)})`"
            >
              <div
                v-for="(count, i) in poolStats.score.histogram.counts"
                :key="i"
                class="flex-1 rounded-xs bg-slate-400/70 min-h-[2px]"
                :style="{ height: `${(count / scoreHistMax) * 100}%` }"
                :title="scoreHistBinTitle(i, count)"
              />
            </div>
            <div
              v-if="poolStats.derivation_error"
              :title="`Ennustatud tuletusvigade absoluutväärtuste summa top ${poolStats.top_n} üle (n=${poolStats.derivation_error.n}, keskmine ${formatNumber(poolStats.derivation_error.abs_mean_pct, 1)} %). Olemas ainult derivation_error järjestusrežiimis.`"
            >
              <div class="text-[11px] text-gray-400 leading-none">Σ |tuletusviga|</div>
              <div class="font-semibold text-sm">
                {{ formatNumber(poolStats.derivation_error.abs_sum_pct, 1) }} %
              </div>
            </div>
          </div>
          <DataTable
            :value="result.candidates"
            removable-sort
            paginator
            :rows="10"
            data-key="id"
            class="text-sm"
            selection-mode="single"
            @row-click="focusCandidate($event.data)"
          >
            <Column field="rank" header="#" sortable style="width: 4rem" />
            <Column field="address" header="Aadress">
              <template #body="{ data }">
                <span :class="{ 'font-semibold': data.was_selected }">{{ data.address || '–' }}</span>
                <span v-if="data.asum" class="text-gray-400"> · {{ data.asum }}</span>
              </template>
            </Column>
            <Column field="tehingusumma" header="Hind" sortable>
              <template #body="{ data }">{{ formatPrice(data.tehingusumma) }}</template>
            </Column>
            <Column header="€/m²">
              <template #body="{ data }">{{ pricePerSqm(data) ? formatPrice(pricePerSqm(data)) : '–' }}</template>
            </Column>
            <Column field="derived_price" sortable>
              <template #header>
                <span title="Objekti hinnahinnang ainult selle võrdlustehingu põhjal: tehingusumma × (1 + Σ kohandused, ümardamata). Protsent = erinevus mudeli toorennustusest. Valitud ridade kaalutud keskmine ≈ Kaalutud keskmine (kohandustabel ümardab 5% sammuga).">Tuletatud hind</span>
              </template>
              <template #body="{ data }">
                <template v-if="data.derived_price != null">
                  <span>{{ formatPrice(data.derived_price) }}</span>
                  <span
                    v-if="derivedDeltaPct(data) != null"
                    class="text-xs font-medium ml-1"
                    :class="pctColor(derivedDeltaPct(data))"
                    :title="`Erinevus mudeli toorennustusest (${formatPrice(derivedReference)}). Kohandus kokku: ${signedPct(data.total_adjustment_pct) ?? '–'}`"
                  >{{ signedPct(derivedDeltaPct(data)) }}</span>
                  <div v-if="derivedPricePerSqm(data)" class="text-xs text-gray-400">{{ formatPrice(derivedPricePerSqm(data)) }}/m²</div>
                  <div
                    v-if="data.indexed_value != null && data.index_factor != null && Math.abs(data.index_factor - 1) > 0.002"
                    class="text-xs text-gray-400"
                    :title="`Ajakohastatud müügikuu turuindeksilt tänasele (×${data.index_factor.toFixed(3)})`"
                  >
                    aja: {{ formatPrice(data.indexed_value) }}
                  </div>
                  <div
                    v-if="data.derivation_error_pct != null"
                    class="text-xs"
                    :title="'Tuletusvea mudeli ennustus: kui kaugele see üksik-tuletus tõenäoliselt tegelikust maandub (järjestusvõti = |väärtus|)'"
                  >
                    <span class="text-gray-400">viga:</span>
                    <span class="font-medium ml-0.5" :class="pctColor(data.derivation_error_pct)">{{ signedPct(data.derivation_error_pct) }}</span>
                    <template v-if="data.derivation_corrected_value != null">
                      <span class="text-gray-400 ml-2" title="Kallutusparandusega hinnang: tuletatud hind / (1 + ennustatud viga)">korr:</span>
                      <span class="font-medium ml-0.5 text-gray-600">{{ formatPrice(data.derivation_corrected_value) }}</span>
                    </template>
                  </div>
                  <div
                    v-else-if="data.derivation_rank_score != null"
                    class="text-xs"
                    :title="'Järjestusmudeli (YetiRank) skoor: suurem = usaldusväärsem võrdlus. Ühikuta — mitte protsent.'"
                  >
                    <span class="text-gray-400">skoor:</span>
                    <span class="font-medium ml-0.5 text-gray-600">{{ formatNumber(data.derivation_rank_score, 3) }}</span>
                  </div>
                </template>
                <span v-else>–</span>
              </template>
            </Column>
            <Column field="ebm_prediction" header="EBM hind" sortable>
              <template #body="{ data }">
                <template v-if="data.ebm_prediction != null">
                  <span>{{ formatPrice(data.ebm_prediction) }}</span>
                  <span v-if="ebmDeltaPct(data) != null" class="text-xs font-medium ml-1" :class="pctColor(ebmDeltaPct(data))">{{ signedPct(ebmDeltaPct(data)) }}</span>
                </template>
                <span v-else>–</span>
              </template>
            </Column>
            <Column field="geo_distance_m" header="Kaugus" sortable>
              <template #body="{ data }">{{ formatMeters(data.geo_distance_m) }}</template>
            </Column>
            <Column field="embed_distance" header="Tunnuste kaugus" sortable>
              <template #body="{ data }">{{ formatNumber(data.embed_distance, 3) }}</template>
            </Column>
            <Column field="price_penalty" header="Hinnatrahv" sortable>
              <template #body="{ data }">{{ formatNumber(data.price_penalty, 3) }}</template>
            </Column>
            <Column field="enrichment_penalty" header="Rik. trahv" sortable>
              <template #body="{ data }">{{ formatNumber(data.enrichment_penalty, 2) }}</template>
            </Column>
            <Column field="score" header="Koondskoor" sortable>
              <template #body="{ data }">
                <span class="font-mono">{{ formatNumber(data.score, 3) }}</span>
              </template>
            </Column>
            <Column field="was_selected" header="Valitud" sortable style="width: 5rem">
              <template #body="{ data }">
                <i v-if="data.was_selected" class="pi pi-check-circle text-green-600"></i>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <!-- Subject feature inspector: hierarchical contribution explorer over the
           verbatim /v2/analyze payload, so the one-hots and interaction terms
           behind a surprising adjustment can be inspected without tailing the API. -->
      <FeatureContributionsDialog
        v-model:visible="featuresOpen"
        :subject-label="subject?.label ?? ''"
        :subject-features="result?.subject_features ?? null"
        :contributions="result?.subject_contributions ?? null"
        :top-contributions="result?.top_contributions ?? null"
      />
    </div>
  </div>
</template>
